<template>
  <div class="blog-lookbook-index">
    <div class="posts">
      <a
        v-for="(post, i) in localItems"
        :key="post.url || i"
        class="post"
        :class="{
          'is-landscape': post._w && post._h && post._w > post._h,
          'is-portrait': post._w && post._h && post._w <= post._h,
        }"
        :href="post.url"
      >
        <figure
          class="post__thumb"
          :data-w="post._w || ''"
          :data-h="post._h || ''"
          v-if="post.imageUrl"
        >
          <img
            :src="post.imageUrl || ''"
            :alt="post.imageAlt || post.title"
            @load="onImgLoad(i, $event)"
          />
        </figure>

        <div class="post__meta">
          <!-- Title + Date row -->
          <div class="post__meta-row">
            <h2 class="post__title">
              <span class="post__counter">{{ (i + 1).toString().padStart(2, '0') }}</span>
              {{ post.title }}
            </h2>
            <div class="post__date-wrap">
              <span class="post__date">{{ post.date }}</span>
              <span class="post__view" aria-hidden="true">VIEW</span>
            </div>
          </div>

          <!-- Excerpt -->
          <p class="post__excerpt" v-html="post.excerpt"></p>
        </div>
      </a>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref } from 'vue'

type Item = {
  title: string
  excerpt: string
  date: string
  url: string
  imageUrl?: string | null
  imageAlt?: string | null
  imageWidth?: number | null
  imageHeight?: number | null
}

const props = defineProps<{ items: Item[] }>()
// make a local, augmentable copy
const localItems = ref(
  props.items.map((p) => ({ ...p, _w: p.imageWidth ?? null, _h: p.imageHeight ?? null }))
)

// fill in natural sizes if Liquid didn’t provide width/height
function onImgLoad(index: number, e: Event) {
  const img = e.target as HTMLImageElement
  const it = localItems.value[index]
  if (it) {
    it._w = img.naturalWidth
    it._h = img.naturalHeight
  }
}
</script>

<style lang="scss">
@import '~styles/base.scss';

.layout-container.blog-lookbook {
  --header-offset: 125px;
}

.blog-lookbook-index {
  @include wrapper;
  padding: 0 var(--inset);

  @include breakpoint('l') {
    padding: 0 0 !important;
  }

  .posts {
    display: flex;
    flex-direction: column;
    gap: 200px;
    max-width: 900px;
    margin: 0;
  }

  .post {
    position: relative;
    display: block;
    text-decoration: none;
    color: inherit;

    /* keep the wrapper width tied to orientation */
    &.is-landscape {
      max-width: 700px;
      margin: auto;
    }
    &.is-portrait {
      max-width: 400px;
      margin: auto;
    }

    /* Image */
    &__thumb {
      width: 100%;
      aspect-ratio: auto;
      overflow: hidden;
      border: 1px solid transparent;
      transition: border-color 0.3s ease;

      img {
        width: 100%;
        height: auto;
        display: block;
      }
    }

    /* Meta (row + excerpt) */
    &__meta {
      position: relative;
      margin-top: 8px;
    }

    /* Title (left) + date & VIEW (right) on one row */
    &__meta-row {
      display: grid;
      grid-template-columns: 1fr 1fr; // only one column now
      gap: 8px;
      margin-bottom: 10px;
      max-width: 400px;
    }

    &__title {
      margin: 0;
      @include type-xs-uppercase;
      display: flex;
    }

    &__counter {
      @include type-xs-uppercase;
      margin-right: 24px;
    }

    &__date-wrap {
      display: inline-flex;
      align-items: baseline;
      gap: 10px;
      white-space: nowrap;
      @include type-xs;
    }

    &__date {
      @include type-xs;
    }

    /* VIEW appears on hover */
    &__view {
      position: absolute;
      right: 0;
      top: -1px;
      @include type-xxs-uppercase;
      opacity: 0;
      transition: opacity 0.3s ease;
    }
    &:hover .post__view {
      opacity: 1;
      transform: translateY(0);
    }

    &:hover .post__thumb {
      border-color: black;
    }

    /* Excerpt under the row */
    &__excerpt {
      margin: 8px 0 0;
      max-width: 400px;
      @include type-xs;
    }
  }

  @include breakpoint('l') {
    .posts {
      gap: 240px;
      margin: 0 auto;
    }
  }

  @include breakpoint('l') {
    .post__meta-row {
      grid-template-columns: 1fr 1fr; // only one column now
      gap: 8px;
    }
    .post__date-wrap {
      justify-content: flex-start;
    }
  }
}
</style>
